/**
 * A small Markdown parser for item descriptions, mission goals and comments. It returns a tree
 * the Markdown component renders as React elements: there is no HTML string anywhere, so raw
 * HTML in the source is shown as text and can never run. Links keep only safe schemes.
 *
 * Blocks: headings, paragraphs, fenced code, block quotes, callouts (> [!NOTE] ...), GFM
 * tables, bullet, numbered and task lists (nested by indentation), rules. Inline: code, bold, italic, strikethrough, links, images,
 * <autolinks>, bare https:// links, hard line breaks (a newline inside a paragraph).
 */

export type MdInline =
  | { type: 'text'; text: string }
  | { type: 'code'; text: string }
  | { type: 'strong' | 'em' | 'del'; children: MdInline[] }
  | { type: 'link'; href: string; children: MdInline[] }
  | { type: 'image'; src: string; alt: string }
  | { type: 'br' };

export type MdBlock =
  | { type: 'heading'; level: 1 | 2 | 3 | 4 | 5 | 6; children: MdInline[] }
  | { type: 'paragraph'; children: MdInline[] }
  | { type: 'code'; lang: string; text: string }
  | { type: 'quote'; children: MdBlock[] }
  | { type: 'callout'; kind: MdCalloutKind; children: MdBlock[] }
  | { type: 'table'; align: MdAlign[]; head: MdInline[][]; rows: MdInline[][][] }
  | { type: 'list'; ordered: boolean; start: number; items: MdListItem[] }
  | { type: 'hr' };

export type MdAlign = 'left' | 'center' | 'right' | null;
export type MdCalloutKind = 'note' | 'tip' | 'important' | 'warning' | 'caution';

export interface MdListItem {
  /** null: not a task; true or false: a checkbox. */
  checked: boolean | null;
  children: MdBlock[];
}

/** Sources longer than this are cut (the hub caps bodies well below it). */
const MAX_SOURCE = 100_000;
const MAX_DEPTH = 8;

/**
 * The link target when it is safe to follow, else null: http(s), mailto, attachment:<id>, a
 * path on this hub (/...) or an anchor (#...). Never javascript:, data:, vbscript:, file: or
 * a protocol-relative //host.
 */
export function safeHref(raw: string): string | null {
  const url = raw.trim().replace(/[\u0000- \u007f-\u009f]/g, (c) => (c === ' ' ? '%20' : ''));
  if (!url) return null;
  if (url.startsWith('#')) return url;
  if (url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\')) return url;
  const m = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(url);
  if (!m) return null;
  const scheme = m[1]!.toLowerCase();
  if (scheme === 'http' || scheme === 'https') return /^https?:\/\/[^/\\\s]/i.test(url) ? url : null;
  if (scheme === 'mailto' || scheme === 'attachment') return url;
  return null;
}

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

/** Linear-time patterns only: every group is followed by something that cannot overlap it. */
const FENCE_RE = /^ {0,3}(`{3,}|~{3,})(.*)$/;
const FENCE = { test: (l: string) => fenceOf(l) !== null };
function fenceOf(line: string): { mark: string; lang: string } | null {
  const m = FENCE_RE.exec(line);
  if (!m) return null;
  const rest = m[2]!;
  // A backtick fence's info string has no backtick (else it is inline code).
  if (m[1]![0] === '`' && rest.includes('`')) return null;
  return { mark: m[1]!, lang: /^\s*([\w+#.-]{0,40})/.exec(rest)?.[1] ?? '' };
}
const HEADING = /^ {0,3}(#{1,6})(?: +(.*))?$/;
const HR = { test: (l: string) => /^ {0,3}[-*_]/.test(l) && /^([-*_])\1{2,}$/.test(l.replace(/\s+/g, '')) };
const QUOTE = /^ {0,3}>\s?/;
const LIST = /^( {0,3})([-*+]|\d{1,9}[.)])(\s+|$)(.*)$/;

/** A table separator row: | --- | :---: | ---: | (pipes at the ends optional). */
const TABLE_SEP = /^ {0,3}\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;
const CALLOUT = /^\[!(note|tip|important|warning|caution)\]\s*$/i;

/** The cells of one table row: split on pipes that are not escaped or inside `code`. */
export function tableCells(line: string): string[] {
  let t = line.trim();
  if (t.startsWith('|')) t = t.slice(1);
  if (t.endsWith('|') && !t.endsWith('\\|')) t = t.slice(0, -1);
  const cells: string[] = [];
  let cur = '';
  let code = false;
  for (let i = 0; i < t.length; i++) {
    const c = t[i]!;
    if (c === '\\' && t[i + 1] === '|') {
      cur += '|';
      i++;
      continue;
    }
    if (c === '`') code = !code;
    if (c === '|' && !code) {
      cells.push(cur.trim());
      cur = '';
      continue;
    }
    cur += c;
  }
  cells.push(cur.trim());
  return cells;
}

/** Whether lines[i] starts a table: a row with a pipe, then a separator with as many columns. */
function tableAt(lines: readonly string[], i: number): boolean {
  const head = lines[i];
  const sep = lines[i + 1];
  if (!head || !sep || !head.includes('|') || !sep.includes('-') || !TABLE_SEP.test(sep)) return false;
  return tableCells(sep).length === tableCells(head).length;
}

const startsBlock = (line: string) => FENCE.test(line) || HEADING.test(line) || HR.test(line) || QUOTE.test(line) || LIST.test(line);

export function parseMarkdown(source: string): MdBlock[] {
  const text = (source ?? '').slice(0, MAX_SOURCE).replace(/\r\n?/g, '\n').replace(/\t/g, '    ');
  return parseBlocks(text.split('\n'), 0);
}

function parseBlocks(lines: string[], depth: number): MdBlock[] {
  const out: MdBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    if (depth >= MAX_DEPTH) {
      out.push({ type: 'paragraph', children: parseInline(lines.slice(i).join('\n')) });
      break;
    }
    const fence = fenceOf(line);
    if (fence) {
      const mark = fence.mark;
      const body: string[] = [];
      i++;
      while (i < lines.length) {
        const l = lines[i]!;
        const close = /^ {0,3}(`{3,}|~{3,})(.*)$/.exec(l);
        if (close && !close[2]!.trim() && close[1]![0] === mark[0] && close[1]!.length >= mark.length) {
          i++;
          break;
        }
        body.push(l);
        i++;
      }
      out.push({ type: 'code', lang: fence.lang, text: body.join('\n') });
      continue;
    }
    const h = HEADING.exec(line);
    if (h) {
      // Trailing spaces and a closing run of #s go (trimEnd and a bounded, anchored strip: linear).
      const text = (h[2] ?? '').trimEnd();
      const closing = /(^| )#+$/.exec(text.slice(-64));
      out.push({ type: 'heading', level: h[1]!.length as 1, children: parseInline(closing ? text.slice(0, text.length - closing[0].length).trimEnd() : text) });
      i++;
      continue;
    }
    if (HR.test(line)) {
      out.push({ type: 'hr' });
      i++;
      continue;
    }
    if (QUOTE.test(line)) {
      const body: string[] = [];
      while (i < lines.length && lines[i]!.trim() && (QUOTE.test(lines[i]!) || !startsBlock(lines[i]!))) {
        body.push(lines[i]!.replace(QUOTE, ''));
        i++;
      }
      const callout = CALLOUT.exec(body[0]?.trim() ?? '');
      if (callout) out.push({ type: 'callout', kind: callout[1]!.toLowerCase() as MdCalloutKind, children: parseBlocks(body.slice(1), depth + 1) });
      else out.push({ type: 'quote', children: parseBlocks(body, depth + 1) });
      continue;
    }
    if (tableAt(lines, i)) {
      const head = tableCells(line);
      const align = tableCells(lines[i + 1]!).map((c): MdAlign => (c.startsWith(':') && c.endsWith(':') ? 'center' : c.endsWith(':') ? 'right' : c.startsWith(':') ? 'left' : null));
      const rows: MdInline[][][] = [];
      i += 2;
      while (i < lines.length && lines[i]!.trim() && lines[i]!.includes('|') && !FENCE.test(lines[i]!)) {
        const cells = tableCells(lines[i]!);
        rows.push(head.map((_, k) => parseInline(cells[k] ?? '', depth + 1)));
        i++;
      }
      out.push({ type: 'table', align, head: head.map((h) => parseInline(h, depth + 1)), rows });
      continue;
    }
    const li = LIST.exec(line);
    if (li) {
      const r = parseList(lines, i, depth);
      out.push(r.block);
      i = r.next;
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && (!para.length || (!startsBlock(lines[i]!) && !tableAt(lines, i)))) {
      para.push(lines[i]!.trim());
      i++;
    }
    out.push({ type: 'paragraph', children: parseInline(para.join('\n')) });
  }
  return out;
}

function parseList(lines: string[], from: number, depth: number): { block: MdBlock; next: number } {
  const first = LIST.exec(lines[from]!)!;
  const ordered = /\d/.test(first[2]!);
  const start = ordered ? Math.min(1e9, parseInt(first[2]!, 10)) : 1;
  const items: MdListItem[] = [];
  let i = from;
  while (i < lines.length) {
    const m = LIST.exec(lines[i]!);
    if (!m || /\d/.test(m[2]!) !== ordered) break;
    const indent = m[1]!.length + m[2]!.length + Math.max(1, Math.min(4, m[3]!.length));
    const body: string[] = [m[4]!];
    i++;
    let blank = false;
    while (i < lines.length) {
      const l = lines[i]!;
      if (!l.trim()) {
        blank = true;
        body.push('');
        i++;
        continue;
      }
      const lead = l.length - l.trimStart().length;
      if (lead >= indent || (lead >= 2 && LIST.test(l.trimStart()))) {
        body.push(l.slice(Math.min(lead, indent)));
        blank = false;
        i++;
        continue;
      }
      // A lazy continuation of the item's paragraph.
      if (!blank && !startsBlock(l)) {
        body.push(l.trim());
        i++;
        continue;
      }
      break;
    }
    while (body.length && !body[body.length - 1]!.trim()) body.pop();
    let checked: boolean | null = null;
    const task = /^\[([ xX])\]\s+/.exec(body[0] ?? '');
    if (task) {
      checked = task[1] !== ' ';
      body[0] = body[0]!.slice(task[0].length);
    }
    items.push({ checked, children: parseBlocks(body, depth + 1) });
    // A blank line followed by something that is not an item ends the list.
    if (i < lines.length && !LIST.test(lines[i]!) && lines[i - 1] !== undefined && !lines[i - 1]!.trim()) break;
  }
  return { block: { type: 'list', ordered, start, items }, next: i };
}

// ---------------------------------------------------------------------------
// Inline
// ---------------------------------------------------------------------------

const ESCAPABLE = /[\\`*_{}[\]()#+\-.!~>|<]/;

/**
 * Inline Markdown. Delimiters without a closer are text. For each delimiter the position past
 * which no closer exists is remembered, so unmatched runs cost linear time, not quadratic.
 */
export function parseInline(src: string, depth = 0): MdInline[] {
  const out: MdInline[] = [];
  let buf = '';
  const flush = () => {
    if (buf) out.push({ type: 'text', text: buf });
    buf = '';
  };
  if (depth > MAX_DEPTH) return [{ type: 'text', text: src }];
  const noCloser: Record<string, number> = {};
  const findCloser = (delim: string, from: number, ok: (at: number) => boolean): number => {
    if (noCloser[delim] !== undefined && from >= noCloser[delim]!) return -1;
    let at = src.indexOf(delim, from);
    while (at >= 0 && !ok(at)) at = src.indexOf(delim, at + 1);
    if (at < 0) noCloser[delim] = Math.min(noCloser[delim] ?? Infinity, from);
    return at;
  };
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    const prev = i > 0 ? src[i - 1]! : ' ';
    if (c === '\\' && i + 1 < src.length) {
      if (src[i + 1] === '\n') {
        flush();
        out.push({ type: 'br' });
        i += 2;
        continue;
      }
      if (ESCAPABLE.test(src[i + 1]!)) {
        buf += src[i + 1];
        i += 2;
        continue;
      }
    }
    if (c === '\n') {
      flush();
      out.push({ type: 'br' });
      i++;
      continue;
    }
    if (c === '`') {
      let n = 1;
      while (src[i + n] === '`') n++;
      const ticks = '`'.repeat(n);
      const end = findCloser(ticks, i + n, (at) => src[at + n] !== '`' && src[at - 1] !== '`');
      if (end >= 0) {
        flush();
        let code = src.slice(i + n, end).replace(/\n/g, ' ');
        if (/^ .* $/.test(code) && code.trim()) code = code.slice(1, -1);
        out.push({ type: 'code', text: code });
        i = end + n;
        continue;
      }
      buf += ticks;
      i += n;
      continue;
    }
    if (c === '!' && src[i + 1] === '[') {
      const l = parseLink(src, i + 1);
      if (l) {
        flush();
        out.push({ type: 'image', src: l.url, alt: l.text });
        i = l.end;
        continue;
      }
    }
    if (c === '[') {
      const l = parseLink(src, i);
      if (l) {
        flush();
        const href = safeHref(l.url);
        const children = parseInline(l.text, depth + 1);
        if (href) out.push({ type: 'link', href, children });
        else out.push(...children);
        i = l.end;
        continue;
      }
    }
    if (c === '<') {
      const m = /^<((?:https?|mailto):[^\s<>]+)>/i.exec(src.slice(i, i + 2048));
      if (m && safeHref(m[1]!)) {
        flush();
        out.push({ type: 'link', href: safeHref(m[1]!)!, children: [{ type: 'text', text: m[1]! }] });
        i += m[0].length;
        continue;
      }
    }
    if ((c === 'h' || c === 'H') && !/[\w/]/.test(prev)) {
      const m = /^https?:\/\/[^\s<>"]+/i.exec(src.slice(i, i + 2048));
      if (m) {
        let url = m[0];
        // Trailing punctuation belongs to the sentence; a ')' only when unbalanced.
        while (/[.,;:!?'"*_~]$/.test(url) || (url.endsWith(')') && (url.match(/\(/g)?.length ?? 0) < (url.match(/\)/g)?.length ?? 0))) url = url.slice(0, -1);
        const href = safeHref(url);
        if (href) {
          flush();
          out.push({ type: 'link', href, children: [{ type: 'text', text: url }] });
          i += url.length;
          continue;
        }
      }
    }
    if ((c === '*' || c === '_' || c === '~') && src[i + 1] === c) {
      const d = c + c;
      const intraword = c === '_' && /\w/.test(prev);
      const next = src[i + 2];
      if (!intraword && next && !/\s/.test(next)) {
        const end = findCloser(d, i + 3, (at) => !/\s/.test(src[at - 1]!) && !(c === '_' && /\w/.test(src[at + 2] ?? '')));
        if (end >= 0) {
          flush();
          out.push({ type: c === '~' ? 'del' : 'strong', children: parseInline(src.slice(i + 2, end), depth + 1) });
          i = end + 2;
          continue;
        }
      }
      buf += d;
      i += 2;
      continue;
    }
    if (c === '*' || c === '_') {
      const intraword = c === '_' && /\w/.test(prev);
      const next = src[i + 1];
      if (!intraword && next && !/\s/.test(next)) {
        const end = findCloser(c, i + 2, (at) => src[at + 1] !== c && src[at - 1] !== c && !/\s/.test(src[at - 1]!) && !(c === '_' && /\w/.test(src[at + 1] ?? '')));
        if (end >= 0) {
          flush();
          out.push({ type: 'em', children: parseInline(src.slice(i + 1, end), depth + 1) });
          i = end + 1;
          continue;
        }
      }
    }
    buf += c;
    i++;
  }
  flush();
  return out;
}

/** [text](url "title") starting at `at` (the '['). */
function parseLink(src: string, at: number): { text: string; url: string; end: number } | null {
  let depthB = 0;
  let j = at;
  const limit = Math.min(src.length, at + 4000);
  for (; j < limit; j++) {
    const ch = src[j];
    if (ch === '\\') {
      j++;
      continue;
    }
    if (ch === '[') depthB++;
    else if (ch === ']') {
      depthB--;
      if (depthB === 0) break;
    } else if (ch === '\n' && src[j + 1] === '\n') return null;
  }
  if (j >= limit || src[j] !== ']' || src[j + 1] !== '(') return null;
  const text = src.slice(at + 1, j);
  let k = j + 2;
  while (src[k] === ' ') k++;
  let url = '';
  if (src[k] === '<') {
    const close = src.indexOf('>', k);
    if (close < 0 || close - k > 2048) return null;
    url = src.slice(k + 1, close);
    k = close + 1;
  } else {
    let parens = 0;
    const s = k;
    for (; k < src.length && k - s < 2048; k++) {
      const ch = src[k]!;
      if (ch === '\\') {
        k++;
        continue;
      }
      if (/\s/.test(ch)) break;
      if (ch === '(') parens++;
      else if (ch === ')') {
        if (!parens) break;
        parens--;
      }
    }
    url = src.slice(s, k);
  }
  while (src[k] === ' ') k++;
  if (src[k] === '"' || src[k] === "'") {
    const q = src[k]!;
    const close = src.indexOf(q, k + 1);
    if (close < 0) return null;
    k = close + 1;
    while (src[k] === ' ') k++;
  }
  if (src[k] !== ')') return null;
  return { text, url: url.replace(/\\([()])/g, '$1'), end: k + 1 };
}

// ---------------------------------------------------------------------------
// Streaming and tables
// ---------------------------------------------------------------------------

/**
 * Markdown still being written, made safe to render: an unclosed `**` or backtick on the last
 * line is closed, a half-written link shows as its words, and a table whose separator row has
 * not arrived yet waits (a lone header row would flash as a line of pipes). Text inside an
 * open code fence is left alone: the parser already reads it as code to the end.
 */
export function healPartial(src: string): string {
  if (((src.match(/^ {0,3}(```|~~~)/gm) ?? []).length) % 2 === 1) return src;
  const lines = src.split('\n');
  const n = lines.length;
  const last = lines[n - 1] ?? '';
  const prev = lines[n - 2] ?? '';
  if (last.includes('|') && !prev.includes('|')) lines.pop();
  else if (prev.includes('|') && /^\s*\|?[\s:|-]*$/.test(last) && last.trim() && !tableAt(lines, n - 2)) {
    // Header and half a separator: hold both back.
    lines.splice(n - 2, 2);
  }
  let text = lines.join('\n');
  // A link or image whose target is still arriving: keep its words.
  text = text.replace(/!?\[([^\]\n]*)\]\([^)\n]*$/, '$1').replace(/!?\[([^\]\n]*)$/, '$1');
  const tail = text.slice(text.lastIndexOf('\n') + 1);
  if ((tail.match(/`/g) ?? []).length % 2 === 1) return `${text}\``;
  if ((tail.match(/\*\*/g) ?? []).length % 2 === 1) return /\*\*\s*$/.test(text) ? text.replace(/\*\*\s*$/, '') : `${text}**`;
  return text;
}

const NUMERIC_CELL = /^[+\-−(]?[$€£¥]?\s?\d[\d.,]*\s?(%|[kKmMbB]|ms|s|h|[A-Z]{3})?\)?$/;

/** A cell that reads as one number: "12", "$4.20", "38%", "1.2K", "450 ms", "(3)". */
export function isNumericText(text: string): boolean {
  return NUMERIC_CELL.test(text.trim());
}

/** Which columns hold numbers (most filled cells numeric): they align right in tabular figures. */
export function numericColumns(rows: readonly (readonly string[])[], columns: number): boolean[] {
  return Array.from({ length: columns }, (_, k) => {
    const cells = rows.map((r) => (r[k] ?? '').trim()).filter(Boolean);
    return cells.length > 0 && cells.filter(isNumericText).length / cells.length >= 0.6;
  });
}

/** The plain text of inline nodes (for alt text and titles). */
export function inlineText(nodes: MdInline[]): string {
  return nodes
    .map((n) => (n.type === 'text' || n.type === 'code' ? n.text : n.type === 'br' ? ' ' : n.type === 'image' ? n.alt : inlineText(n.children)))
    .join('');
}
